/**
 * Elite Batch 24 Part 3 — Support & Customer Success (Workflows 21-30)
 *
 * PRODUCTION-GRADE ENHANCEMENTS:
 * 1. Case stamping — every workflow starts with caseStampNode (node 2)
 * 2. Confidence-gated AI — aiNodeV2 outputs confidence_score, evidence, needs_human_review
 * 3. Confidence routing — confidenceGate routes high-confidence auto vs low-confidence human
 * 4. Post-execution verification — verifyNode confirms actions actually worked
 * 5. Exception handling / logging — sheetsNode or exceptionNode for audit trail
 *
 * CORRECT builder field names:
 * - conditionData (NOT conditionDetails)
 * - approvalGateData (NOT approvalDetails)
 * - delayAction (NOT delayDetails)
 * - code (NOT runCodeDetails.code)
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED HELPERS — BATCH 24 ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function delayNode(
  id: string,
  label: string,
  y: number,
  time: number,
  timeFormat: string,
  x = 80,
): any {
  return n(
    id,
    "delayAction",
    label,
    y,
    {
      delayAction: { time, timeFormat },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 21-30: Support & Customer Success ============

export const ELITE_BATCH24_PART3 = [
  // ─── 21. Elite: Ticket Intake + AI First Response ───
  {
    name: "Elite: Ticket Intake + AI First Response",
    description:
      "Automatically classifies incoming Zendesk tickets by intent, drafts a context-aware first response using AI, and posts it if confidence is high — otherwise routes to an agent for review before responding.",
    category: "Support & Customer Success",
    tags: [
      "zendesk",
      "ai-response",
      "ticket-intake",
      "auto-reply",
      "customer-support",
    ],
    nodes: [
      trigger("Zendesk: New ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts: "Fires when a new support ticket is created in Zendesk",
        setup:
          "Connect Zendesk webhook to trigger on ticket.created events. Payload includes ticket_id, subject, description, requester_email, priority, and tags.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "ticket_intake_ai_response",
      ),
      aiNodeV2(
        "3",
        "AI: Classify intent + draft response",
        280,
        "You are an expert support agent. Given a ticket subject and description, classify the intent (billing, technical, account, general, bug_report, feature_request) and draft a helpful first response. Be empathetic and specific. Include relevant KB article links if applicable.",
        "Ticket #{variables.ticket_id} — Subject: {variables.subject} — Description: {variables.description} — Customer: {variables.requester_email} — Priority: {variables.priority}",
        [
          { field: "intent", as: "ticket_intent", type: "string" },
          { field: "draft_response", as: "ai_draft_response", type: "string" },
          { field: "suggested_tags", as: "ai_suggested_tags", type: "string" },
          {
            field: "escalation_needed",
            as: "ai_escalation_flag",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Zendesk + HubSpot: Customer lookup", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/users/{variables.requester_id}.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "user.name", customName: "customer_name", type: "string" },
            { name: "user.tags", customName: "customer_tags", type: "string" },
            {
              name: "user.organization_id",
              customName: "org_id",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: auto-reply threshold", 520, "80"),
      n("6", "hitApi", "Zendesk: Post AI response to ticket", 640, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/tickets/{variables.ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"comment":{"body":"{variables.ai_draft_response}","public":true},"tags":["{variables.ai_suggested_tags}"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "updated_ticket_id",
              type: "string",
            },
            {
              name: "ticket.status",
              customName: "ticket_status",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Agent review: low-confidence response",
        640,
        "AI drafted a response for ticket #{variables.ticket_id} with {variables.ai_confidence}% confidence. Intent: {variables.ticket_intent}. Please review the draft before it is posted.",
        4,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Ticket updated with response",
        760,
        "GET",
        "https://{zendesk_domain}.zendesk.com/api/v2/tickets/{variables.ticket_id}/comments.json",
        [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
        [
          {
            name: "comments[-1].body",
            customName: "last_comment_body",
            type: "string",
          },
          { name: "count", customName: "comment_count", type: "number" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Response posted successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Ticket response audit trail",
        1000,
        "TicketIntake",
        '[["{variables.case_id}","{variables.ticket_id}","{variables.ticket_intent}","{variables.ai_confidence}","{variables.ai_evidence}","{variables.customer_name}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 22. Elite: SLA Breach Prevention ───
  {
    name: "Elite: SLA Breach Prevention",
    description:
      "Runs on a schedule to analyze open Zendesk tickets, uses AI to predict which tickets are at risk of SLA breach based on age, priority, and response patterns, then proactively alerts agents before violations occur.",
    category: "Support & Customer Success",
    tags: [
      "sla",
      "zendesk",
      "breach-prevention",
      "proactive-support",
      "scheduling",
    ],
    nodes: [
      trigger("Schedule: SLA breach check every 30 minutes", "schedule", {
        schedule: "*/30 * * * *",
        howItStarts:
          "Runs every 30 minutes to scan for tickets approaching SLA breach",
        setup:
          "Configure schedule for desired check frequency. Ensure Zendesk API credentials are set with read access to tickets and SLA policies.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "sla_breach_prevention",
      ),
      aiNodeV2(
        "3",
        "AI: Predict SLA breach risk for open tickets",
        280,
        "You are an SLA management expert. Given a list of open tickets with their creation time, priority, current status, and SLA targets, identify tickets at risk of breaching their SLA. For each at-risk ticket, provide ticket_id, breach_probability (0-100), time_remaining_minutes, recommended_action, and assigned_agent.",
        "Open tickets data: {variables.open_tickets_json} — SLA policy: First reply: P1=1hr, P2=4hr, P3=8hr, P4=24hr. Resolution: P1=4hr, P2=12hr, P3=24hr, P4=72hr. Current time: {variables.current_timestamp}",
        [
          { field: "at_risk_tickets", as: "at_risk_tickets", type: "string" },
          { field: "total_at_risk", as: "total_at_risk", type: "number" },
          {
            field: "highest_priority_ticket",
            as: "highest_priority_ticket",
            type: "string",
          },
          { field: "summary", as: "risk_summary", type: "string" },
        ],
      ),
      n("4", "hitApi", "Zendesk: Fetch open ticket SLA data", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/tickets.json?query=type:ticket+status:open+status:pending&sort_by=created_at&sort_order=asc",
          headers: [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
          body: [],
          variables: [
            {
              name: "tickets",
              customName: "open_tickets_json",
              type: "string",
            },
            { name: "count", customName: "open_ticket_count", type: "number" },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: breach prediction accuracy",
        520,
        "75",
      ),
      slackNode(
        "6",
        "Slack: Alert agents about at-risk tickets",
        640,
        "🚨 *SLA Breach Alert* — {variables.total_at_risk} ticket(s) at risk\\n\\n{variables.risk_summary}\\n\\nHighest priority: {variables.highest_priority_ticket}\\n\\nConfidence: {variables.ai_confidence}% | Case: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "Support lead: Review breach predictions",
        640,
        "AI identified {variables.total_at_risk} tickets at risk of SLA breach with {variables.ai_confidence}% confidence. Summary: {variables.risk_summary}. Approve to send team alerts.",
        2,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Slack alerts delivered",
        760,
        "POST",
        "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        [{ key: "Content-Type", value: "application/json" }],
        [{ name: "ok", customName: "alert_delivered", type: "boolean" }],
      ),
      conditionNode(
        "9",
        "Check: Alerts sent successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: SLA breach prevention audit",
        1000,
        "SLABreach",
        '[["{variables.case_id}","{variables.total_at_risk}","{variables.risk_summary}","{variables.ai_confidence}","{variables.ai_evidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 23. Elite: Escalation Risk Detector ───
  {
    name: "Elite: Escalation Risk Detector",
    description:
      "Monitors customer interaction events for frustration signals and churn indicators. AI analyzes sentiment, interaction history, and customer value to flag accounts at risk of escalation, alerting the assigned CSM before issues compound.",
    category: "Support & Customer Success",
    tags: [
      "escalation",
      "churn-prevention",
      "sentiment-analysis",
      "csm-alerting",
      "hubspot",
    ],
    nodes: [
      trigger("Interaction event: Negative signal detected", "webhook_app", {
        source: "intercom",
        event: "conversation.updated",
        howItStarts:
          "Fires when a customer interaction event contains negative sentiment or repeated contact signals",
        setup:
          "Connect Intercom or support platform webhook for conversation updates. Payload includes conversation_id, customer_email, message_count, sentiment_hint, and contact_reason.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "escalation_risk_detector",
      ),
      aiNodeV2(
        "3",
        "AI: Analyze frustration + churn signals",
        280,
        "You are a customer success expert specializing in churn prevention. Analyze the interaction data for frustration signals: repeated contacts (3+ in 7 days), negative language, unresolved issues, product complaints, cancellation mentions, competitor references. Score escalation_risk (0-100), identify the primary_frustration_driver, and recommend an intervention strategy.",
        "Customer: {variables.customer_email} — Conversation #{variables.conversation_id} — Message count: {variables.message_count} — Latest message: {variables.latest_message} — Contact history (7 days): {variables.recent_contact_count} — Sentiment: {variables.sentiment_hint}",
        [
          {
            field: "escalation_risk",
            as: "escalation_risk_score",
            type: "number",
          },
          {
            field: "primary_frustration_driver",
            as: "frustration_driver",
            type: "string",
          },
          {
            field: "intervention_strategy",
            as: "recommended_intervention",
            type: "string",
          },
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch customer value + history", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{variables.customer_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.annualrevenue",
              customName: "customer_arr",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: escalation risk accuracy",
        520,
        "75",
      ),
      slackNode(
        "6",
        "Slack: Alert CSM of escalation risk",
        640,
        "⚠️ *Escalation Risk Alert*\\n\\nCustomer: {variables.customer_email}\\nRisk Score: {variables.escalation_risk_score}/100\\nFrustration Driver: {variables.frustration_driver}\\nARR: ${variables.customer_arr}\\nRecommended Action: {variables.recommended_intervention}\\nChurn Probability: {variables.churn_probability}\\n\\nCase: {variables.case_id} | Confidence: {variables.ai_confidence}%",
      ),
      approvalNode(
        "7",
        "CS manager: Review escalation alert",
        640,
        "AI flagged {variables.customer_email} as escalation risk ({variables.escalation_risk_score}/100) with {variables.ai_confidence}% confidence. Driver: {variables.frustration_driver}. Approve to alert the CSM.",
        4,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Alert delivered to CSM",
        760,
        "POST",
        "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        [{ key: "Content-Type", value: "application/json" }],
        [{ name: "ok", customName: "csm_alert_sent", type: "boolean" }],
      ),
      conditionNode(
        "9",
        "Check: CSM alert delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Escalation risk audit trail",
        1000,
        "EscalationRisk",
        '[["{variables.case_id}","{variables.customer_email}","{variables.escalation_risk_score}","{variables.frustration_driver}","{variables.customer_arr}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 24. Elite: CSAT Recovery Workflow ───
  {
    name: "Elite: CSAT Recovery Workflow",
    description:
      "Triggers on low CSAT scores to automatically analyze the root cause, look up the customer in HubSpot, and generate a personalized recovery outreach email. High-confidence recovery plans execute automatically; uncertain cases go to the CSM for review.",
    category: "Support & Customer Success",
    tags: [
      "csat",
      "recovery",
      "customer-retention",
      "hubspot",
      "email-outreach",
    ],
    nodes: [
      trigger("Low CSAT score submitted", "webhook_app", {
        source: "zendesk",
        event: "satisfaction_rating.created",
        howItStarts:
          "Fires when a customer submits a CSAT rating of 1 or 2 (bad/unsatisfactory)",
        setup:
          "Configure Zendesk webhook for satisfaction_rating.created events filtered to scores 1-2. Payload includes ticket_id, rating, comment, requester_email, and assignee_id.",
        inputFields: [],
      }),
      caseStampNode("2", "Stamp case ID + metadata", 160, "csat_recovery"),
      aiNodeV2(
        "3",
        "AI: Analyze root cause + recommend recovery",
        280,
        "You are a customer experience recovery specialist. Given a low CSAT rating with the customer comment and ticket history, identify the root_cause (agent_error, slow_resolution, wrong_answer, rude_tone, process_issue, product_bug), draft a personalized recovery_email that acknowledges the specific issue, and recommend a recovery_action (discount, escalation, callback, account_credit, personal_apology).",
        "Ticket #{variables.ticket_id} — CSAT Score: {variables.rating} — Customer Comment: {variables.csat_comment} — Customer: {variables.requester_email} — Original Issue: {variables.ticket_subject} — Resolution Time: {variables.resolution_hours}h",
        [
          { field: "root_cause", as: "root_cause", type: "string" },
          {
            field: "recovery_email",
            as: "recovery_email_body",
            type: "string",
          },
          { field: "recovery_action", as: "recovery_action", type: "string" },
          { field: "severity", as: "issue_severity", type: "string" },
        ],
      ),
      n("4", "hitApi", "HubSpot: Lookup customer profile", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{variables.requester_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "customer_first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lastname",
              customName: "customer_last_name",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: recovery recommendation",
        520,
        "75",
      ),
      emailNode(
        "6",
        "Email: Send personalized recovery outreach",
        640,
        "{variables.requester_email}",
        "We want to make this right — RE: {variables.ticket_subject}",
        "{variables.recovery_email_body}",
      ),
      approvalNode(
        "7",
        "CSM review: Low-confidence recovery plan",
        640,
        "AI recommends recovery for {variables.requester_email} after CSAT score {variables.rating}. Root cause: {variables.root_cause}. Action: {variables.recovery_action}. Confidence: {variables.ai_confidence}%. Please review before sending.",
        8,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Recovery email sent",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={variables.requester_email}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "emails_sent_count",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "email_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Recovery email delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: CSAT recovery audit trail",
        1000,
        "CSATRecovery",
        '[["{variables.case_id}","{variables.ticket_id}","{variables.rating}","{variables.root_cause}","{variables.recovery_action}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 25. Elite: Knowledge Gap Detector ───
  {
    name: "Elite: Knowledge Gap Detector",
    description:
      "Runs on a weekly schedule to cluster unanswered or frequently-asked ticket themes, identifies gaps in the knowledge base, and automatically creates content tasks in Asana for the documentation team to address.",
    category: "Support & Customer Success",
    tags: ["knowledge-base", "content-gaps", "zendesk", "asana", "scheduling"],
    nodes: [
      trigger("Schedule: Weekly knowledge gap analysis", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to analyze the past week of tickets for knowledge gaps",
        setup:
          "Configure Zendesk API for ticket export and Asana API for task creation. Ensure the knowledge base API is accessible for current article inventory.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "knowledge_gap_detector",
      ),
      aiNodeV2(
        "3",
        "AI: Cluster unanswered themes + identify gaps",
        280,
        "You are a knowledge management expert. Analyze the past week of support tickets to identify recurring themes that lack adequate KB coverage. Cluster tickets by topic, count frequency, identify which topics have no matching KB article, and rank gaps by impact (ticket volume * avg resolution time). Output top 5 gaps with suggested_article_title, description, priority, and estimated_ticket_deflection.",
        "Tickets from last 7 days: {variables.recent_tickets_json} — Current KB articles: {variables.kb_article_titles} — Unanswered ticket count: {variables.unanswered_count}",
        [
          {
            field: "knowledge_gaps",
            as: "knowledge_gaps_json",
            type: "string",
          },
          { field: "total_gaps_found", as: "total_gaps", type: "number" },
          { field: "highest_impact_gap", as: "top_gap_title", type: "string" },
          {
            field: "estimated_weekly_deflection",
            as: "estimated_deflection",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Zendesk + KB: Fetch ticket and article data", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/search.json?query=type:ticket+created>7days+status:solved&sort_by=created_at",
          headers: [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
          body: [],
          variables: [
            {
              name: "results",
              customName: "recent_tickets_json",
              type: "string",
            },
            { name: "count", customName: "ticket_count", type: "number" },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: gap identification accuracy",
        520,
        "75",
      ),
      n("6", "hitApi", "Asana: Create content tasks for gaps", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"KB Gap: {variables.top_gap_title}","projects":["{asana_kb_project_id}"],"notes":"Knowledge gap identified by AI analysis.\\n\\nGaps found: {variables.total_gaps}\\nEstimated deflection: {variables.estimated_deflection}\\n\\nFull gap analysis:\\n{variables.knowledge_gaps_json}","due_on":"{variables.due_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            { name: "data.name", customName: "task_name", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "Content lead: Review gap analysis",
        640,
        "AI identified {variables.total_gaps} knowledge base gaps with {variables.ai_confidence}% confidence. Top gap: {variables.top_gap_title}. Estimated weekly deflection: {variables.estimated_deflection}. Approve to create Asana tasks.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Asana tasks created",
        760,
        "GET",
        "https://app.asana.com/api/1.0/tasks/{variables.asana_task_id}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          { name: "data.gid", customName: "verified_task_id", type: "string" },
          {
            name: "data.completed",
            customName: "task_completed",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Tasks created successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Knowledge gap audit trail",
        1000,
        "KnowledgeGaps",
        '[["{variables.case_id}","{variables.total_gaps}","{variables.top_gap_title}","{variables.estimated_deflection}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 26. Elite: Duplicate Ticket Merger ───
  {
    name: "Elite: Duplicate Ticket Merger",
    description:
      "When a new ticket arrives, AI fingerprints it and searches for similar open tickets in Zendesk. At 90%+ confidence it auto-merges duplicates; below that threshold an agent confirms the match before merging to prevent false positives.",
    category: "Support & Customer Success",
    tags: [
      "duplicate-detection",
      "zendesk",
      "ticket-merge",
      "ai-matching",
      "efficiency",
    ],
    nodes: [
      trigger("Zendesk: New ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created, triggering duplicate detection",
        setup:
          "Connect Zendesk webhook for ticket.created events. Ensure API access for ticket search and merge operations.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "duplicate_ticket_merger",
      ),
      aiNodeV2(
        "3",
        "AI: Fingerprint + match similar tickets",
        280,
        "You are a support operations expert specializing in duplicate detection. Given a new ticket and a list of existing open tickets, identify potential duplicates by comparing subject, description keywords, requester, and category. For each match, provide the matching_ticket_id, similarity_score (0-100), matching_signals (what made them similar), and merge_recommendation (merge, review, or no_match).",
        "New ticket #{variables.ticket_id}: Subject='{variables.subject}' Description='{variables.description}' Requester={variables.requester_email} — Existing open tickets: {variables.open_tickets_json}",
        [
          {
            field: "best_match_ticket_id",
            as: "match_ticket_id",
            type: "string",
          },
          { field: "similarity_score", as: "similarity_score", type: "number" },
          { field: "matching_signals", as: "matching_signals", type: "string" },
          {
            field: "merge_recommendation",
            as: "merge_recommendation",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Zendesk: Search for similar open tickets", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/search.json?query=type:ticket+status:open+requester:{variables.requester_email}",
          headers: [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
          body: [],
          variables: [
            {
              name: "results",
              customName: "open_tickets_json",
              type: "string",
            },
            {
              name: "count",
              customName: "similar_ticket_count",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: duplicate match (90% threshold)",
        520,
        "90",
      ),
      n("6", "hitApi", "Zendesk: Auto-merge duplicate tickets", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/tickets/{variables.ticket_id}/merge.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ids", value: "[{variables.match_ticket_id}]" },
            {
              key: "target_comment",
              value:
                "Auto-merged by AI duplicate detection. Similarity: {variables.similarity_score}%. Signals: {variables.matching_signals}",
            },
          ],
          variables: [
            {
              name: "job_status.id",
              customName: "merge_job_id",
              type: "string",
            },
            {
              name: "job_status.status",
              customName: "merge_status",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Agent: Confirm duplicate match for merge",
        640,
        "AI detected ticket #{variables.ticket_id} as a potential duplicate of #{variables.match_ticket_id} with {variables.similarity_score}% similarity. Signals: {variables.matching_signals}. Approve to merge.",
        4,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Merge completed",
        760,
        "GET",
        "https://{zendesk_domain}.zendesk.com/api/v2/tickets/{variables.ticket_id}.json",
        [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
        [
          {
            name: "ticket.status",
            customName: "merged_ticket_status",
            type: "string",
          },
          {
            name: "ticket.id",
            customName: "verified_ticket_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Merge completed successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Duplicate merge audit trail",
        1000,
        "DuplicateMerge",
        '[["{variables.case_id}","{variables.ticket_id}","{variables.match_ticket_id}","{variables.similarity_score}","{variables.matching_signals}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 27. Elite: Return Eligibility Processor ───
  {
    name: "Elite: Return Eligibility Processor",
    description:
      "Processes incoming return requests by validating eligibility against Shopify order data, using AI to determine if the return meets policy criteria. Eligible returns get an automatic return label; edge cases go to support for manual review.",
    category: "Support & Customer Success",
    tags: ["returns", "shopify", "eligibility", "e-commerce", "automation"],
    nodes: [
      trigger("Return request submitted", "webhook_app", {
        source: "shopify",
        event: "returns.request_created",
        howItStarts:
          "Fires when a customer submits a return request through the storefront or support channel",
        setup:
          "Connect Shopify webhook for return request events. Payload includes order_id, customer_email, product_ids, return_reason, and order_date.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "return_eligibility_processor",
      ),
      aiNodeV2(
        "3",
        "AI: Validate eligibility + recommend action",
        280,
        "You are a returns policy expert. Given an order and return request, validate: (1) within return window (30 days from delivery), (2) item condition matches policy (unused, defective, wrong_item), (3) not a restricted category, (4) no prior return abuse. Output eligibility (eligible, ineligible, review_needed), reason, recommended_action (full_refund, exchange, store_credit, deny), and if applicable, restocking_fee_percent.",
        "Order #{variables.order_id} — Customer: {variables.customer_email} — Order date: {variables.order_date} — Delivery date: {variables.delivery_date} — Return reason: {variables.return_reason} — Product: {variables.product_name} — Price: ${variables.product_price} — Prior returns count: {variables.prior_return_count}",
        [
          { field: "eligibility", as: "return_eligibility", type: "string" },
          { field: "reason", as: "eligibility_reason", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "restocking_fee_percent",
            as: "restocking_fee",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Shopify: Lookup order details", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{variables.order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.created_at",
              customName: "order_date",
              type: "string",
            },
            {
              name: "order.fulfillments[0].shipment_status",
              customName: "shipment_status",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: eligibility determination",
        520,
        "75",
      ),
      emailNode(
        "6",
        "Email: Send return label + instructions",
        640,
        "{variables.customer_email}",
        "Your return for order #{variables.order_id} has been approved",
        "Hi,\\n\\nYour return request for order #{variables.order_id} has been approved.\\n\\nAction: {variables.recommended_action}\\nReason: {variables.eligibility_reason}\\n\\nYour return shipping label is attached. Please ship the item within 14 days.\\n\\nReturn ID: {variables.case_id}\\n\\nThank you for your patience.",
      ),
      approvalNode(
        "7",
        "Support: Review edge-case return",
        640,
        "AI assessed return for order #{variables.order_id} as '{variables.return_eligibility}' with {variables.ai_confidence}% confidence. Reason: {variables.eligibility_reason}. Action: {variables.recommended_action}. Please review and approve.",
        8,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Return created in Shopify",
        760,
        "GET",
        "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{variables.order_id}.json",
        [{ key: "X-Shopify-Access-Token", value: "{shopify_token}" }],
        [
          { name: "order.refunds", customName: "refund_data", type: "string" },
          {
            name: "order.financial_status",
            customName: "financial_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Return processed successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Return processing audit trail",
        1000,
        "Returns",
        '[["{variables.case_id}","{variables.order_id}","{variables.return_eligibility}","{variables.recommended_action}","{variables.restocking_fee}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 28. Elite: Outage Customer Communication ───
  {
    name: "Elite: Outage Customer Communication",
    description:
      "Triggers on PagerDuty incidents to draft customer-facing notifications, identify affected customers via HubSpot, and dispatch multi-channel communications. High-confidence notifications go out immediately; sensitive incidents require incident commander approval.",
    category: "Support & Customer Success",
    tags: [
      "incident-response",
      "pagerduty",
      "customer-comms",
      "outage",
      "statuspage",
    ],
    nodes: [
      trigger("PagerDuty: Incident triggered", "webhook_app", {
        source: "pagerduty",
        event: "incident.triggered",
        howItStarts:
          "Fires when a PagerDuty incident is triggered, indicating a potential customer-facing outage",
        setup:
          "Connect PagerDuty webhook for incident.triggered events. Payload includes incident_id, title, severity, service_name, created_at, and urgency.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "outage_customer_communication",
      ),
      aiNodeV2(
        "3",
        "AI: Draft incident notification",
        280,
        "You are an incident communications expert. Given an incident details, draft a professional customer-facing notification that is transparent but reassuring. Include: incident_summary (2 sentences, no jargon), estimated_impact (what customers might experience), status (investigating, identified, monitoring, resolved), next_update_eta, and affected_services. Tone should be empathetic, professional, and action-oriented.",
        "Incident #{variables.incident_id}: {variables.incident_title} — Severity: {variables.severity} — Service: {variables.service_name} — Started: {variables.created_at} — Urgency: {variables.urgency} — StatusPage current: {variables.statuspage_status}",
        [
          {
            field: "incident_summary",
            as: "notification_summary",
            type: "string",
          },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          {
            field: "notification_body",
            as: "notification_body",
            type: "string",
          },
          {
            field: "affected_services",
            as: "affected_services",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "StatusPage + HubSpot: Get affected customers", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"hs_product_name","operator":"CONTAINS_TOKEN","value":"{variables.service_name}"}]}]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "affected_customer_count",
              type: "number",
            },
            {
              name: "results",
              customName: "affected_customers_json",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: notification quality", 520, "75"),
      emailNode(
        "6",
        "Email: Dispatch incident notification",
        640,
        "{variables.affected_customers_email_list}",
        "[{variables.severity}] Service Update: {variables.incident_title}",
        "{variables.notification_body}\\n\\nAffected Services: {variables.affected_services}\\nEstimated Impact: {variables.estimated_impact}\\n\\nWe will provide updates as the situation develops. Track status: https://status.yourcompany.com\\n\\nIncident ID: {variables.incident_id}",
      ),
      approvalNode(
        "7",
        "Incident commander: Review notification",
        640,
        "AI drafted an outage notification for incident #{variables.incident_id} ({variables.severity}) affecting {variables.affected_customer_count} customers. Confidence: {variables.ai_confidence}%. Review the notification before dispatch.",
        1,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Notifications dispatched",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=incident-{variables.incident_id}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "notifications_sent",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "dispatch_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Notifications sent successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Incident communication audit",
        1000,
        "OutageComms",
        '[["{variables.case_id}","{variables.incident_id}","{variables.severity}","{variables.affected_customer_count}","{variables.notification_summary}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 29. Elite: Agent QA Sampler ───
  {
    name: "Elite: Agent QA Sampler",
    description:
      "Runs on a daily schedule to sample recently resolved Zendesk tickets, evaluates agent response quality using AI (tone, accuracy, resolution, empathy), and posts QA scores to Slack. Low-confidence evaluations are sent to the QA manager for calibration.",
    category: "Support & Customer Success",
    tags: ["quality-assurance", "agent-qa", "zendesk", "scoring", "scheduling"],
    nodes: [
      trigger("Schedule: Daily QA sampling at 8 AM", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to sample and evaluate yesterday's resolved tickets for quality",
        setup:
          "Configure Zendesk API access for reading resolved tickets. Set sample size (default 10% or max 20 tickets). Ensure Slack webhook for QA channel is configured.",
        inputFields: [],
      }),
      caseStampNode("2", "Stamp case ID + metadata", 160, "agent_qa_sampler"),
      aiNodeV2(
        "3",
        "AI: Evaluate sampled tickets for quality",
        280,
        "You are a support quality assurance expert. Evaluate each sampled ticket interaction on these dimensions (each 1-10): tone_score (professionalism, empathy), accuracy_score (correct information given), resolution_score (was the issue actually solved), efficiency_score (minimal back-and-forth). Provide an overall_score (weighted average), top_performer (agent with highest avg), improvement_areas (common weaknesses), and coaching_suggestions.",
        "Sampled resolved tickets (last 24h): {variables.sampled_tickets_json} — Sample size: {variables.sample_size} — Agent roster: {variables.agent_list}",
        [
          { field: "overall_score", as: "qa_overall_score", type: "number" },
          { field: "top_performer", as: "top_performer_agent", type: "string" },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
          {
            field: "coaching_suggestions",
            as: "coaching_suggestions",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Zendesk: Fetch resolved tickets sample", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/search.json?query=type:ticket+status:solved+solved>1day&sort_by=updated_at&sort_order=desc&per_page=20",
          headers: [{ key: "Authorization", value: "Bearer {zendesk_token}" }],
          body: [],
          variables: [
            {
              name: "results",
              customName: "sampled_tickets_json",
              type: "string",
            },
            { name: "count", customName: "sample_size", type: "number" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: QA evaluation accuracy", 520, "75"),
      slackNode(
        "6",
        "Slack: Post QA scores to team channel",
        640,
        "📊 *Daily Agent QA Report*\\n\\nOverall Score: {variables.qa_overall_score}/10\\nTop Performer: {variables.top_performer_agent}\\nSample Size: {variables.sample_size} tickets\\n\\n*Improvement Areas:*\\n{variables.improvement_areas}\\n\\n*Coaching Suggestions:*\\n{variables.coaching_suggestions}\\n\\nConfidence: {variables.ai_confidence}% | Case: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "QA manager: Calibrate low-confidence scores",
        640,
        "AI evaluated {variables.sample_size} tickets with {variables.ai_confidence}% confidence. Overall score: {variables.qa_overall_score}/10. Please review and calibrate before posting to team.",
        8,
        420,
      ),
      verifyNode(
        "8",
        "Verify: QA scores posted to Slack",
        760,
        "POST",
        "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        [{ key: "Content-Type", value: "application/json" }],
        [{ name: "ok", customName: "qa_posted", type: "boolean" }],
      ),
      conditionNode(
        "9",
        "Check: QA report posted successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: QA sampling audit trail",
        1000,
        "AgentQA",
        '[["{variables.case_id}","{variables.sample_size}","{variables.qa_overall_score}","{variables.top_performer_agent}","{variables.improvement_areas}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 30. Elite: Service Recovery Offer ───
  {
    name: "Elite: Service Recovery Offer",
    description:
      "Triggers on negative customer experiences to assess severity, look up customer value in HubSpot, and generate an appropriate recovery offer. Offers under $100 auto-send; higher-value offers require manager approval to control budget exposure.",
    category: "Support & Customer Success",
    tags: [
      "service-recovery",
      "customer-retention",
      "offers",
      "hubspot",
      "approval-chain",
    ],
    nodes: [
      trigger("Negative experience event detected", "webhook_app", {
        source: "zendesk",
        event: "ticket.tagged",
        howItStarts:
          "Fires when a ticket is tagged with 'negative_experience', 'complaint', or 'service_failure' in Zendesk",
        setup:
          "Configure Zendesk trigger to fire webhook when tickets receive negative experience tags. Payload includes ticket_id, requester_email, tags, subject, and description.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "service_recovery_offer",
      ),
      aiNodeV2(
        "3",
        "AI: Assess severity + recommend offer",
        280,
        "You are a customer retention expert. Assess the severity of the negative experience (low, medium, high, critical) based on the issue type, customer sentiment, and impact. Recommend a recovery_offer: for low severity suggest a personal apology email, for medium a 10-20% discount code, for high a $25-50 account credit, for critical a $50-100+ credit plus executive callback. Output severity, recommended_offer_type, offer_value_usd, personalized_message, and business_justification.",
        "Ticket #{variables.ticket_id}: {variables.ticket_subject} — Description: {variables.ticket_description} — Customer: {variables.requester_email} — Tags: {variables.ticket_tags} — Customer ARR: ${variables.customer_arr} — Lifetime value: ${variables.customer_ltv} — Tenure: {variables.customer_tenure_months} months",
        [
          { field: "severity", as: "experience_severity", type: "string" },
          { field: "recommended_offer_type", as: "offer_type", type: "string" },
          { field: "offer_value_usd", as: "offer_value", type: "number" },
          {
            field: "personalized_message",
            as: "recovery_message",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch customer value + history", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{variables.requester_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.annualrevenue",
              customName: "customer_arr",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_id",
              type: "string",
            },
            {
              name: "results[0].properties.notes_last_updated",
              customName: "customer_ltv",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: severity assessment", 520, "75"),
      emailNode(
        "6",
        "Email: Send recovery offer",
        640,
        "{variables.requester_email}",
        "We value you — a gesture from our team",
        "{variables.recovery_message}\\n\\nAs a token of our commitment to your experience, we would like to offer you: {variables.offer_type} (${variables.offer_value}).\\n\\nThis offer is valid for 30 days. No action needed — it has been applied to your account.\\n\\nRecovery ID: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "Manager: Approve $100+ recovery offers",
        640,
        "AI recommends a {variables.offer_type} worth ${variables.offer_value} for {variables.requester_email} (severity: {variables.experience_severity}). Customer ARR: ${variables.customer_arr}. Confidence: {variables.ai_confidence}%. Approve to send the recovery offer.",
        4,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Recovery offer email sent",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={variables.requester_email}&tag=recovery&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "offer_emails_sent",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "offer_email_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Offer email delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Service recovery audit trail",
        1000,
        "ServiceRecovery",
        '[["{variables.case_id}","{variables.ticket_id}","{variables.experience_severity}","{variables.offer_type}","{variables.offer_value}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },
];
